import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FluidForm } from '../../../shared';
import { SimpleFormProvider } from '../../fluidComponents/fluidForm/SimpleFormLoader';
import { ExampleAuthWrapper } from '../ExampleAuthWrapper';
import { useFluidForm } from '../../fluidComponents/fluidForm/FormContext';
import { API_URL } from '../../http.client';
// @TODO: FLU-193 Handle this.
const formConfig = {
    key: 'realtime-voice-agent-demo',
    label: 'Realtime Voice Agent Demo',
    type: 'form',
    validationStrictness: 'strict',
    fields: [
        { key: 'fullName', label: 'Full name', type: 'fullName', required: true },
        { key: 'email', label: 'Email', type: 'email', required: true, unique: true },
        { key: 'phone', label: 'Phone', type: 'phone' },
        { key: 'birthDate', label: 'Birth date', type: 'date' },
        {
            key: 'contactPreference',
            label: 'How should we contact you?',
            type: 'options',
            options: [
                { text: 'Email', value: 'email' },
                { text: 'Phone', value: 'phone' },
            ],
        },
        { key: 'notes', label: 'Anything else?', type: 'text' },
    ],
};
export function RealtimeVoiceAgentPage() {
    return (React.createElement(ExampleAuthWrapper, { formConfig: formConfig },
        React.createElement(RealtimeVoiceAgentExample, null)));
}
function RealtimeVoiceAgentExample() {
    const submissionId = useMemo(() => `realtime-voice-${crypto.randomUUID()}`, []);
    const form = useMemo(() => new FluidForm(formConfig, {}, {
        styleConfig: {
            // Keep a normal form UI visible while voice agent updates data.
            style: 'fluid',
            layout: 'form',
            inputModes: ['chat', 'speech', 'camera', 'fileUpload', 'urlCapture'],
        },
    }), []);
    return (React.createElement("div", { className: "space-y-5" },
        React.createElement("header", null,
            React.createElement("h1", { className: "text-xl font-semibold text-gray-900" }, "Realtime Voice Agent (speech-to-speech)"),
            React.createElement("p", { className: "text-gray-600 text-sm mt-1" },
                "Speak naturally. The agent asks the next missing field and uses tool calling to continuously update",
                ' ',
                React.createElement("code", { className: "px-1 bg-gray-100 rounded" }, "submission.data"),
                ". The live JSON panel updates as you talk.")),
        React.createElement("div", { className: "grid grid-cols-12 gap-5 items-start" },
            React.createElement("div", { className: "col-span-7 rounded-lg border border-gray-200 bg-white p-3" },
                React.createElement(SimpleFormProvider, { form: form, submissionId: submissionId, autosave: true })),
            React.createElement("div", { className: "col-span-5" },
                React.createElement(VoiceAgentPanel, { formKey: formConfig.key, submissionId: submissionId })))));
}
function VoiceAgentPanel({ formKey, submissionId }) {
    const form = useFluidForm();
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState(null);
    const [jsonPreview, setJsonPreview] = useState(() => JSON.stringify(form.getFormSubmission(), null, 2));
    const [lastEventType, setLastEventType] = useState('');
    const [lastToolCall, setLastToolCall] = useState(null);
    const pcRef = useRef(null);
    const dcRef = useRef(null);
    const localStreamRef = useRef(null);
    const remoteAudioRef = useRef(null);
    // Map call_id -> function name
    const callNameById = useRef({});
    useEffect(() => {
        form.onUpdated(() => {
            setJsonPreview(JSON.stringify(form.getFormSubmission(), null, 2));
        });
    }, [form]);
    const stop = async () => {
        setError(null);
        setStatus('idle');
        try {
            dcRef.current?.close();
        }
        catch { }
        dcRef.current = null;
        try {
            pcRef.current?.close();
        }
        catch { }
        pcRef.current = null;
        if (localStreamRef.current) {
            for (const t of localStreamRef.current.getTracks())
                t.stop();
            localStreamRef.current = null;
        }
    };
    const sendEvent = (evt) => {
        const dc = dcRef.current;
        if (!dc || dc.readyState !== 'open')
            return;
        dc.send(JSON.stringify(evt));
    };
    const handleToolCall = async (callId, name, argsJson) => {
        let args;
        try {
            args = JSON.parse(argsJson);
        }
        catch {
            args = null;
        }
        setLastToolCall({ callId, name, args });
        if (name === 'updateSubmissionFields') {
            const patch = args?.data;
            const targetSubmissionId = args?.submissionId || submissionId;
            const r = await fetch(`${API_URL}/api/fluid-form/realtime/tools/update-fields`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ submissionId: targetSubmissionId, data: patch }),
            });
            const payload = await r.json().catch(() => ({}));
            if (!r.ok) {
                throw new Error(payload?.message || 'Failed to update fields');
            }
            const updatedData = payload?.data || {};
            form.mergeSubmissionData(updatedData);
            sendEvent({
                type: 'conversation.item.create',
                item: {
                    type: 'function_call_output',
                    call_id: callId,
                    output: JSON.stringify({ ok: true, submissionId: targetSubmissionId, data: updatedData }),
                },
            });
            // Prompt the model to continue after tool output.
            sendEvent({ type: 'response.create' });
            return;
        }
        if (name === 'getSubmissionData') {
            const targetSubmissionId = args?.submissionId || submissionId;
            const r = await fetch(`${API_URL}/api/fluid-form/realtime/tools/submission/${encodeURIComponent(targetSubmissionId)}`, {
                method: 'GET',
                credentials: 'include',
            });
            const payload = await r.json().catch(() => ({}));
            if (!r.ok) {
                throw new Error(payload?.message || 'Failed to fetch submission');
            }
            sendEvent({
                type: 'conversation.item.create',
                item: {
                    type: 'function_call_output',
                    call_id: callId,
                    output: JSON.stringify({ ok: true, submissionId: targetSubmissionId, data: payload?.data || {} }),
                },
            });
            sendEvent({ type: 'response.create' });
            return;
        }
        // Unknown tool: respond with an error so the model can recover.
        sendEvent({
            type: 'conversation.item.create',
            item: { type: 'function_call_output', call_id: callId, output: JSON.stringify({ ok: false, error: 'Unknown tool' }) },
        });
        sendEvent({ type: 'response.create' });
    };
    const start = async () => {
        if (status !== 'idle')
            return;
        setStatus('connecting');
        setError(null);
        callNameById.current = {};
        try {
            const tokenResp = await fetch(`${API_URL}/api/fluid-form/realtime/client-secret/${encodeURIComponent(formKey)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ submissionId }),
            });
            const tokenPayload = await tokenResp.json().catch(() => ({}));
            if (!tokenResp.ok) {
                throw new Error(tokenPayload?.message || 'Failed to create realtime session');
            }
            const clientSecret = tokenPayload?.clientSecret;
            const model = tokenPayload?.model;
            if (!clientSecret || !model) {
                throw new Error('Missing clientSecret/model from backend');
            }
            const pc = new RTCPeerConnection();
            pcRef.current = pc;
            pc.ontrack = (ev) => {
                const audioEl = remoteAudioRef.current;
                if (!audioEl)
                    return;
                const stream = ev.streams?.[0];
                if (!stream)
                    return;
                audioEl.srcObject = stream;
                audioEl.play().catch(() => { });
            };
            const dc = pc.createDataChannel('oai-events');
            dcRef.current = dc;
            dc.onmessage = async (ev) => {
                let msg;
                try {
                    msg = JSON.parse(ev.data);
                }
                catch {
                    return;
                }
                const t = msg?.type;
                if (t)
                    setLastEventType(t);
                // Capture function name when the tool call item is created.
                if (t === 'response.output_item.added' && msg?.item?.type === 'function_call') {
                    const callId = msg?.item?.call_id;
                    const name = msg?.item?.name;
                    if (callId && name)
                        callNameById.current[String(callId)] = String(name);
                    return;
                }
                // Tool call args completed (this event does NOT include the tool name).
                if (t === 'response.function_call_arguments.done') {
                    const callId = String(msg?.call_id || '');
                    const args = String(msg?.arguments || '');
                    const name = callNameById.current[callId] || '';
                    if (!callId || !args || !name)
                        return;
                    try {
                        await handleToolCall(callId, name, args);
                    }
                    catch (e) {
                        setError(e?.message || 'Tool call failed');
                    }
                }
            };
            const localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            localStreamRef.current = localStream;
            for (const track of localStream.getTracks()) {
                pc.addTrack(track, localStream);
            }
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            const sdpResp = await fetch(`https://api.openai.com/v1/realtime?model=${encodeURIComponent(model)}`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${clientSecret}`,
                    'Content-Type': 'application/sdp',
                },
                body: offer.sdp,
            });
            const answerSdp = await sdpResp.text();
            if (!sdpResp.ok || !answerSdp) {
                throw new Error(`Failed SDP exchange (${sdpResp.status})`);
            }
            await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
            setStatus('connected');
        }
        catch (e) {
            await stop();
            setError(e?.message || 'Failed to start voice agent');
            setStatus('idle');
        }
    };
    return (React.createElement("div", { className: "rounded-lg border border-gray-200 bg-white p-4 space-y-3" },
        React.createElement("div", { className: "flex items-center justify-between" },
            React.createElement("div", null,
                React.createElement("div", { className: "font-semibold text-gray-900" }, "Voice Agent"),
                React.createElement("div", { className: "text-xs text-gray-500" },
                    "Status: ",
                    status)),
            React.createElement("div", { className: "flex gap-2" },
                React.createElement("button", { className: "px-3 py-2 rounded-md text-sm font-semibold bg-indigo-600 text-white disabled:opacity-60", onClick: start, disabled: status !== 'idle' }, "Start"),
                React.createElement("button", { className: "px-3 py-2 rounded-md text-sm font-semibold bg-gray-100 text-gray-800 disabled:opacity-60", onClick: stop, disabled: status === 'idle' }, "Stop"))),
        error && React.createElement("div", { className: "text-sm text-red-600" }, error),
        React.createElement("audio", { ref: remoteAudioRef, autoPlay: true }),
        React.createElement("div", { className: "text-xs text-gray-500" },
            "Last event: ",
            lastEventType || '—'),
        lastToolCall && (React.createElement("pre", { className: "text-xs bg-gray-50 border border-gray-100 rounded p-2 overflow-auto max-h-40" }, JSON.stringify(lastToolCall, null, 2))),
        React.createElement("div", null,
            React.createElement("div", { className: "text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2" }, "submission.data (live)"),
            React.createElement("pre", { className: "text-xs bg-gray-50 border border-gray-100 rounded p-3 overflow-auto max-h-[520px]" }, jsonPreview))));
}
export default RealtimeVoiceAgentPage;
